/**
 * The bookmark lists and the tag vocabulary, one member for each request, and the one
 * bookmark the view is cut to.
 *
 * Each list is a capped query of its own filters, so a write names the change and each
 * mounted member reads its rows again. The cases read again too, since a case carries its
 * bookmarks. The bookmark the view is cut to reads again as well, so the view's header and
 * its fence follow an edit, and a deleted bookmark lets the view go.
 */

import { type Atom, atom, type WritableAtom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { queryAtoms } from '../queryAtoms';
import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import {
    type Bookmark, BookmarkChange, type BookmarkListFilter, type BookmarkListSnapshot,
    type BookmarkTag, type BookmarkTagsSnapshot,
} from '@/types/bookmarks';
import type { ViewBookmarkSnapshot } from '@/types/viewScope';

import { refreshCasesAtom } from './casesAtoms';

/** How many of the most used tags the list filter offers. */
const BOOKMARK_TAG_COUNT = 50;

const NOT_CUT: ViewBookmarkSnapshot = { id: null, bookmark: null, isLoading: false, error: null };

const LIST_PENDING: BookmarkListSnapshot = {
    bookmarks: [],
    isLoaded: false,
    isLoading: true,
    error: null,
};

const TAGS_PENDING: BookmarkTagsSnapshot = {
    tags: [],
    isLoading: true,
    error: null,
};

/** The bookmarks API reads them by listing, so the span a bookmark was opened at names it. */
function isSameOpening(one: Bookmark, other: Bookmark): boolean {
    return one.id === other.id && one.deviceId === other.deviceId &&
        one.startTimeMs === other.startTimeMs && one.durationMs === other.durationMs;
}

/** A read of one opened bookmark; set it to read the bookmark again. */
const viewBookmarks = atomFamily(
    (openedBookmark: Bookmark): WritableAtom<ViewBookmarkSnapshot, [], void> => {
        const snapshot = snapshotAtoms<ViewBookmarkSnapshot>(
            { ...NOT_CUT, isLoading: true },
            async (): Promise<ViewBookmarkSnapshot> => {
                const { id, deviceId, startTimeMs, durationMs } = openedBookmark;
                const list = await api.listBookmarks({
                    deviceId,
                    fromMs: startTimeMs,
                    toMs: startTimeMs + Math.max(durationMs, 1),
                });
                return {
                    id,
                    bookmark: list.find((one) => one.id === id) ?? null,
                    isLoading: false,
                    error: null,
                };
            },
        );
        return atom(
            (get): ViewBookmarkSnapshot => get(snapshot.value),
            (_get, set): void => { set(snapshot.reload); },
        );
    },
    isSameOpening,
);

const notCutAtom = atom(NOT_CUT);

const lists = queryAtoms<BookmarkListFilter, BookmarkListSnapshot, Bookmark[], BookmarkChange>({
    getRequestKey: (filter): string => JSON.stringify([
        filter.deviceId, filter.fromMs, filter.toMs, filter.tags, filter.text, filter.limit,
    ]),
    getPending: (): BookmarkListSnapshot => LIST_PENDING,
    load: async (filter): Promise<Bookmark[]> => api.listBookmarks(filter),
    merge: (_previousSnapshot, bookmarks): BookmarkListSnapshot => ({
        bookmarks,
        isLoaded: true,
        isLoading: false,
        error: null,
    }),
});

const tagLists = queryAtoms<number, BookmarkTagsSnapshot, BookmarkTag[], BookmarkChange>({
    getRequestKey: String,
    getPending: (): BookmarkTagsSnapshot => TAGS_PENDING,
    load: async (limit): Promise<BookmarkTag[]> => api.listBookmarkTags(limit),
    merge: (_previousSnapshot, tags): BookmarkTagsSnapshot => ({
        tags,
        isLoading: false,
        error: null,
    }),
});

/** The rows that `filter` lists. */
export function bookmarkListAtom(
    filter: BookmarkListFilter | null,
): Atom<BookmarkListSnapshot> {
    return lists.getMember(filter);
}

/** The tag vocabulary; every reader shares one request. */
export const bookmarkTagsAtom: Atom<BookmarkTagsSnapshot> = tagLists.getMember(BOOKMARK_TAG_COUNT);

export const bookmarksWrittenAtom = atom(null, (_get, set): void => {
    set(lists.applyAll, BookmarkChange.Written);
    set(tagLists.applyAll, BookmarkChange.Written);
    set(refreshCasesAtom);
    for (const openedBookmark of viewBookmarks.getParams()) {
        set(viewBookmarks(openedBookmark));
    }
});

/** The read of the bookmark the view is cut to: `openedBookmark`, or none. */
export function viewBookmarkAtom(openedBookmark: Bookmark | null): Atom<ViewBookmarkSnapshot> {
    return openedBookmark ? viewBookmarks(openedBookmark) : notCutAtom;
}
